import { authedApiFetch } from '../../../utils/Api';

/**
 * Service for managing prompt workflows
 */
const promptWorkflowService = {
	/**
	 * Get the prompt workflow for a specific event type and tenant
	 * @param {string} tenantId - The tenant ID
	 * @param {string} eventType - The event type (e.g., 'deal_creation')
	 * @returns {Promise<Object>} - The prompt workflow with system default info if applicable
	 */
	getPromptWorkflow: async (tenantId, eventType) => {
		// Validate required parameters
		if (!eventType) {
			throw new Error('Event type is required for fetching prompt workflow');
		}

		if (!tenantId) {
			throw new Error('Tenant ID is required for fetching prompt workflow');
		}

		let endpoint = `/tenants/${tenantId}/prompt-workflows/${eventType}`;

		try {
			const response = await authedApiFetch({
				endpoint: endpoint,
				method: 'GET',
			});
			return response;
		} catch (error) {
			console.error('Error fetching prompt workflow:', error);
			throw error;
		}
	},

	/**
	 * Save a prompt workflow
	 * @param {string} tenantId - The tenant ID (null for system default)
	 * @param {Object} workflow - The workflow configuration to save
	 * @returns {Promise<Object>} - The saved workflow
	 */
	savePromptWorkflow: async (tenantId, workflow) => {
		if (!tenantId) {
			throw new Error('Tenant ID is required for saving prompt workflow');
		}

		const endpoint = `/tenants/${tenantId}/prompt-workflows`;

		try {
			const payload = {
				...workflow,
			};

			const response = await authedApiFetch({
				endpoint: endpoint,
				method: 'POST',
				payload: payload,
			});

			return response;
		} catch (error) {
			console.error('Error saving prompt workflow:', error);
			throw error;
		}
	},

	/**
	 * Get all available event types for prompt workflows
	 * @returns {Promise<Array<string>>} - List of available event types
	 */
	getEventTypes: async () => {
		try {
			const response = await authedApiFetch({
				endpoint: '/api/workflows/event-types',
				method: 'GET',
			});
			return response;
		} catch (error) {
			console.error('Error fetching event types:', error);
			throw error;
		}
	},

	/**
	 * Delete a tenant's prompt workflow (revert to system default)
	 * @param {string} tenantId - The tenant ID
	 * @param {string} eventType - The event type (e.g., 'deal_creation')
	 * @returns {Promise<Object>} - The system default workflow
	 */
	deletePromptWorkflow: async (tenantId, eventType) => {
		// Validate required parameters
		if (!eventType) {
			throw new Error('Event type is required for deleting prompt workflow');
		}

		if (!tenantId) {
			throw new Error('Tenant ID is required for deleting prompt workflow');
		}

		const endpoint = `/tenants/${tenantId}/prompt-workflows/${eventType}`;

		try {
			const response = await authedApiFetch({
				endpoint: endpoint,
				method: 'DELETE',
			});
			return response;
		} catch (error) {
			console.error('Error deleting prompt workflow:', error);
			throw error;
		}
	},
};

export default promptWorkflowService;
